// prepareUpload.ts — what happens to a picked file before it leaves the browser.
//
// Two jobs, both here because the server cannot do either: a HEIC (an iPhone
// photo picked from Files, or any desktop pick of one) becomes a JPEG, and a very
// large photo is downscaled so the 25 MB door only ever stops a PDF. Safari
// decodes HEIC natively; everywhere else libheif loads on demand, only once a HEIC
// is actually picked (heic-to, LGPL: libheif compiled to plain JavaScript, run in a
// Worker it builds from a blob URL, so a CSP would need worker-src blob: or the
// package's csp build). PDFs and small images pass through untouched, and nothing
// in here throws at the caller: a photo this code cannot convert goes up as it is,
// and the server keeps it as a failed read with a reason the owner sees, which
// beats a rejection that vanishes with the dialog.

export const MAX_LONG_EDGE = 3000;
export const DOWNSCALE_OVER_BYTES = 2 * 1024 * 1024;
export const JPEG_QUALITY = 0.88;
export const HEIC_DECODE_TIMEOUT_MS = 30_000;

const HEIC_EXT_RE = /\.(heic|heif)$/i;
const HEIC_MIME_RE = /hei[cf]/i;
const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|heic|heif|bmp|tiff?)$/i;
// ISO base media brands an iPhone or a HEIF-writing camera stamps at byte 8.
const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'mif1', 'msf1']);

export interface Decoded {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

export interface PrepareDeps {
  /** Native decode; rejects when this browser cannot read the format. */
  loadImage: (blob: Blob) => Promise<Decoded>;
  /** The libheif path for a HEIC the browser refused. Resolves to a JPEG blob. */
  decodeHeic: (blob: Blob) => Promise<Blob>;
  /** Draw at the given size and encode as JPEG. */
  encodeJpeg: (img: Decoded, width: number, height: number) => Promise<Blob>;
}

export const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
export const looksLikeImage = (file: File) => file.type.startsWith('image/') || IMAGE_EXT_RE.test(file.name);

/** The downscaled size, or the same size when it already fits. */
export function fitWithin(width: number, height: number, max: number = MAX_LONG_EDGE) {
  const long = Math.max(width, height);
  if (long <= max) return { width, height };
  const k = max / long;
  return { width: Math.round(width * k), height: Math.round(height * k) };
}

/** IMG_9290.heic becomes IMG_9290.jpg, so the row still carries the name the person knows. */
export function jpegName(name: string) {
  const stem = name.replace(/\.[^./\\]+$/, '');
  return `${stem || 'receipt'}.jpg`;
}

const readHead = (file: File, bytes: number) =>
  new Promise<Uint8Array>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(new Uint8Array(r.result as ArrayBuffer));
    r.onerror = () => reject(r.error);
    r.readAsArrayBuffer(file.slice(0, bytes));
  });

/** HEIC by type, by extension, or by the container brand when the browser named neither. */
export async function sniffHeic(file: File): Promise<boolean> {
  if (HEIC_MIME_RE.test(file.type) || HEIC_EXT_RE.test(file.name)) return true;
  if (file.type && file.type !== 'application/octet-stream') return false;
  try {
    const head = await readHead(file, 12);
    if (head.length < 12) return false;
    const ascii = (from: number, to: number) => String.fromCharCode(...Array.from(head.slice(from, to)));
    return ascii(4, 8) === 'ftyp' && HEIC_BRANDS.has(ascii(8, 12).toLowerCase());
  } catch {
    return false;
  }
}

const browserDeps: PrepareDeps = {
  loadImage: (blob) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      const release = () => URL.revokeObjectURL(url);
      img.onload = () => resolve({ source: img, width: img.naturalWidth, height: img.naturalHeight, release });
      img.onerror = () => { release(); reject(new Error('undecodable')); };
      img.src = url;
    }),
  // The decoder runs in one shared worker; a worker that dies (out of memory on a
  // huge photo on a phone) never replies, so the race keeps the queue from hanging.
  decodeHeic: async (blob) => {
    const { heicTo } = await import('heic-to');
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('HEIC decode timed out')), HEIC_DECODE_TIMEOUT_MS); });
    try {
      return await Promise.race([heicTo({ blob, type: 'image/jpeg', quality: 0.9 }), timeout]);
    } finally {
      clearTimeout(timer);
    }
  },
  encodeJpeg: (img, width, height) =>
    new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { reject(new Error('no canvas')); return; }
      ctx.fillStyle = '#ffffff'; // JPEG has no alpha; a transparent PNG would otherwise go black
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img.source, 0, 0, width, height);
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode failed'))), 'image/jpeg', JPEG_QUALITY);
    }),
};

/**
 * The file to upload for a picked file. Same object back when nothing needed
 * doing; a fresh JPEG File when the photo was a HEIC or oversized.
 */
export async function prepareForUpload(file: File, deps: PrepareDeps = browserDeps): Promise<File> {
  if (isPdf(file) || !looksLikeImage(file)) return file;
  const heic = await sniffHeic(file);
  if (!heic && file.size <= DOWNSCALE_OVER_BYTES) return file;

  let decoded: Decoded | null = null;
  let converted: Blob | null = null;
  try {
    decoded = await deps.loadImage(file);
  } catch {
    if (!heic) return file; // an odd image this browser cannot read: let the server say so
    try {
      converted = await deps.decodeHeic(file);
      decoded = await deps.loadImage(converted);
    } catch (err) {
      console.warn('[receipts] HEIC conversion skipped:', (err as Error)?.message);
      if (converted) return new File([converted], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
      return file;
    }
  }

  try {
    const size = fitWithin(decoded.width, decoded.height);
    const resized = size.width !== decoded.width || size.height !== decoded.height;
    if (!heic && !resized) return file;
    // libheif already produced a JPEG; re-encoding it only to keep the same size is a lossy pass for nothing.
    if (converted && !resized) return new File([converted], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
    const blob = await deps.encodeJpeg(decoded, size.width, size.height);
    return new File([blob], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
  } catch (err) {
    console.warn('[receipts] downscale skipped:', (err as Error)?.message);
    if (converted) return new File([converted], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
    return file;
  } finally {
    decoded.release();
  }
}
